import React, { useEffect, useRef, useState } from 'react'
import { NavLink, useHistory, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { ReactComponent as Partners } from 'assets/icons/shop.svg'

import Upload, { UploadRef } from 'util/modules/Upload'
import Select from 'util/modules/Form/Select'
import Search from 'util/modules/Pagination/components/Search'

import { Map, MapRef } from 'util/modules/Maps'
import { loadGoogleMaps } from 'util/modules/Maps/googleMaps'
import { getCategoryList } from 'panel/category/redux/actions'
import { ApplicationState } from 'AppReducer'
import { createPartner, getPartner, updatePartner } from 'panel/partner/redux/actions'

const initialState = {
  ...{ distribution: [] as string[], isActive: true },
  ...{ name: '', description: '', deliveryType: '', order: 0, manager: 0, managerName: '', category: 0 },
  ...{ zip: '', city: '', state: '', street: '', number: '', district: '', complement: '', latitude: 0, longitude: 0 }
}

const PartnerForm: React.FC = () => {
  const history = useHistory()
  const [form, setForm] = useState(initialState)

  const [picture, setPicture] = useState<File>()
  const [isFetching, setFetching] = useState(false)

  const [states, setStates] = useState<{ short: string, name: string }[]>([])
  const [cities, setCities] = useState<{ name: string }[]>([])

  const mapRef = useRef<MapRef>(null)
  const uploadRef = useRef<UploadRef>(null)

  const dispatch = useDispatch()
  const { id } = useParams<{ id?: string }>()

  const { categoryList: { items: categories } } = useSelector((state: ApplicationState) => state.categoryReducer)

  function handleClear (): void {
    setForm(initialState)
    history.push('/panel/partner')
    uploadRef.current?.clear()
  }

  async function handleSubmit (e: React.FormEvent): Promise<void> {
    try {
      e.preventDefault()
      setFetching(true)

      if (!id && !picture) throw new Error('Selecione uma imagem')

      const formData = new FormData()
      Object.keys(form).forEach((key) => {
        const value = form[key as 'name']
        if (value) {
          formData.set(key, String(value))
        }
      })

      if (picture) {
        formData.set('picture', picture)
      }

      if (!id) {
        createPartner(formData)(dispatch).then(res => {
          if (res?.status === 'success') {
            handleClear()
          }
        })
      } else {
        updatePartner(id, formData)(dispatch)
      }
      setFetching(false)
    } catch (error) {
      setFetching(false)
      alert(error.message)
    }
  }

  function fetchStates (): void {
    fetch('https://servicodados.ibge.gov.br/api/v1/localidades/estados')
      .then(res => res.json())
      .then(res => {
        if (res && Array.isArray(res)) {
          setStates(res.map(item => ({ short: item.sigla, name: item.nome })))
        }
      })
  }

  function fetchCities (short: string): void {
    if (short) {
      fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${short}/municipios`)
        .then(res => res.json())
        .then(res => {
          if (res && Array.isArray(res)) {
            setCities(res.map(item => ({ name: item.nome })))
          }
        })
    }
  }

  function handleDistribution (e: React.ChangeEvent<HTMLInputElement>): void {
    const { checked, value } = e.target
    if (checked) {
      setForm({ ...form, distribution: [...form.distribution, value] })
    } else {
      const index = form.distribution.findIndex((item) => item === value)
      if (index > -1) {
        setForm({
          ...form,
          distribution: [...form.distribution.slice(0, index), ...form.distribution.slice(index + 1)]
        })
      }
    }
  }

  function fetchCategories (): void {
    getCategoryList()(dispatch)
  }

  useEffect(() => {
    if (mapRef.current) {
      loadGoogleMaps().then(() => {
        mapRef.current?.init()
      })
    }
  }, [mapRef])

  useEffect(() => {
    fetchStates()
    fetchCategories()
  }, [dispatch])

  useEffect(() => {
    if (id && mapRef.current) {
      getPartner(id)(dispatch).then(res => {
        const partner = res?.data
        if (partner) {
          setForm({
            zip: partner.zip || '',
            name: partner.name || '',
            city: partner.city || '',
            order: partner.order || 0,
            state: partner.state || '',
            street: partner.street || '',
            number: partner.number || '',
            district: partner.district || '',
            complement: partner.complement || '',
            description: partner.description || '',
            deliveryType: partner.deliveryType || '',
            isActive: !!partner.isActive,
            category: partner.category?.id || 0,
            manager: partner.manager?.id || 0,
            managerName: partner.manager?.person?.name || '',
            latitude: partner.latitude || 0,
            longitude: partner.longitude || 0,
            distribution: partner.distribution || []
          })

          if (partner.pictureMedium && uploadRef.current) {
            uploadRef.current.setPreviewImage(partner.pictureMedium)
          }

          if (partner.state) {
            fetchCities(partner.state)
          }

          if (mapRef.current && partner.latitude && partner.longitude) {
            mapRef.current.setMarker(partner.latitude, partner.longitude, true)
          }
        }
      })
    } else {
      setForm(initialState)
    }
  }, [id, mapRef, dispatch])

  return (
    <form className="row margin-top-16" onSubmit={handleSubmit}>
      {/* Basic Info */}
      <div className="row panel">
        <h2 className="row justify-start">
          <Partners height="25" width="30" fill="var(--panel-text)" /> Gestão de Parceiro
        </h2>

        <div className="row margin-top-16">
          <div className="row" style={{ width: 170, height: 170 }} >
            <Upload
              ref={uploadRef}
              onChange={setPicture}
              placeholder="Selecionar imagem para categoria"
            />
          </div>
          <div className="grow padding-left-16" style={{ marginTop: -8 }}>
            {/* Line 1 */}
            <div className="row">
              <div className="form-control col-2">
                <label>Ordem</label>
                <input
                  type="number"
                  value={form.order}
                  step={1}
                  min={0}
                  onChange={(e): void => setForm({ ...form, order: parseInt(e.target.value) })}
                  required
                />
              </div>
              <div className="form-control col-6">
                <label>Nome do Parceiro</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e): void => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-control col-4">
                <label className="no-padding-label">
                  Categoria Principal
                  <i className="fa fa-sync" onClick={fetchCategories} />
                </label>
                <Select
                  required
                  selected={String(form.category)}
                  onSelect={(key): void => setForm({ ...form, category: key })}
                  itens={categories.map(({ id, name }) => ({ key: id, value: name }))}
                />
              </div>
            </div>

            {/* Line 2 */}
            <div className="row">
              <div className="form-control col-2">
                <label>Ativo?</label>
                <select
                  value={form.isActive ? 1 : 0}
                  onChange={(e): void => setForm({ ...form, isActive: parseInt(e.target.value) === 1 })}
                  required
                >
                  <option value={1}>Sim</option>
                  <option value={0}>Não</option>
                </select>
              </div>
              <div className="form-control col-6">
                <label>Descrição</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e): void => setForm({ ...form, description: e.target.value })}
                  required
                />
              </div>
              <div className="form-control col-4">
                <label>Proprietário</label>
                <Search
                  required
                  url="/user"
                  label='name'
                  filter='person.name'
                  filterType="filter_like"
                  fields='id,person.name'
                  match={['start']}
                  selected={String(form.manager)}
                  defaultValue={form.managerName}
                  options={{ returnKey: 'id', renderValue: (user) => user.person?.name }}
                  onSelect={(manager): void => setForm({ ...form, manager: Number(manager) })}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="row justify-start margin-top-32">
          <h3 className="margin-bottom-8">Como os pedidos poderão ser finalizados?</h3>
          <label className="checkbox">
            <input
              type="checkbox"
              value="DELIVERY"
              onChange={handleDistribution}
              checked={form.distribution.findIndex((item) => item === 'DELIVERY') > -1}
            />
            <div className="checkmark"/> Via Entregador
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              value="TAKE"
              onChange={handleDistribution}
              checked={form.distribution.findIndex((item) => item === 'TAKE') > -1}
            />
            <div className="checkmark"/> Retirar no local
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              value="AT_PLACE"
              onChange={handleDistribution}
              checked={form.distribution.findIndex((item) => item === 'AT_PLACE') > -1}
            />
            <div className="checkmark"/> No Local
          </label>
        </div>
      </div>

      <div className="row panel margin-top-32">
        <h2 className="row justify-start">
          <Partners height="25" width="30" fill="var(--panel-text)" /> Endereço do Parceiro
        </h2>

        <div className="row margin-top-16">
          <div style={{ width: 255, height: 255 }}>
            <Map
              ref={mapRef}
              mapTypeId="roadmap"
              onMarker={(latitude, longitude): void => setForm(form => ({ ...form, latitude, longitude }))}
            />
          </div>
          <div className="grow padding-left-16" style={{ marginTop: -8 }}>
            {/* Line 1 */}
            <div className="row">
              <div className="form-control col-2">
                <label>CEP</label>
                <input
                  required
                  type="text"
                  value={form.zip}
                  onChange={(e): void => setForm({ ...form, zip: e.target.value })}
                />
              </div>

              <div className="form-control col-5">
                <label>Estado</label>
                <Select
                  required
                  selected={form.state}
                  onSelect={(key): void => {
                    fetchCities(key)
                    setForm({ ...form, state: key, city: '' })
                  }}
                  itens={states.map(({ short, name }) => ({ key: short, value: name }))}
                />
              </div>

              <div className="form-control col-5">
                <label>Cidade</label>
                <Select
                  required
                  selected={form.city}
                  onSelect={(key): void => {
                    setForm({ ...form, city: key })
                  }}
                  itens={cities.map(({ name }) => ({ key: name, value: name }))}
                />
              </div>
            </div>

            {/* Line 2 */}
            <div className="row">
              <div className="form-control col-5">
                <label>Logradouro</label>
                <input
                  type="text"
                  value={form.street}
                  onChange={(e): void => setForm({ ...form, street: e.target.value })}
                  required
                />
              </div>
              <div className="form-control col-2">
                <label>Número</label>
                <input
                  type="text"
                  value={form.number}
                  onChange={(e): void => setForm({ ...form, number: e.target.value })}
                  required
                />
              </div>
              <div className="form-control col-5">
                <label>Bairro</label>
                <input
                  type="text"
                  value={form.district}
                  onChange={(e): void => setForm({ ...form, district: e.target.value })}
                  required
                />
              </div>
            </div>

            {/* Line 3 */}
            <div className="row">
              <div className="form-control col-7">
                <label>Complemento</label>
                <input
                  type="text"
                  value={form.complement}
                  onChange={(e): void => setForm({ ...form, complement: e.target.value })}
                  required
                />
              </div>
              <div className="row col-5">
                <div className="form-control col-6">
                  <label>Latitude</label>
                  <input
                    required
                    type="number"
                    step={0.000001}
                    value={form.latitude}
                    onChange={(e): void => setForm({ ...form, latitude: parseFloat(e.target.value) })}
                  />
                </div>
                <div className="form-control col-6">
                  <label>Longitude</label>
                  <input
                    required
                    type="number"
                    step={0.000001}
                    value={form.longitude}
                    onChange={(e): void => setForm({ ...form, longitude: parseFloat(e.target.value) })}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row margin-top-16" style={{ justifyContent: id ? 'space-between' : 'flex-end' }}>
        {
          !!id &&
          <NavLink to={`/panel/partner/${id}/config`} className="button secondary" style={{ width: 300 }}>
            <i className="fa fa-cogs"/> Configurar
          </NavLink>
        }
        <div className="row" style={{ width: 616 }}>
          <span className="button secondary" style={{ width: 300, marginRight: 16 }} onClick={handleClear}>
            Limpar
          </span>
          <button className="button primary" disabled={isFetching} style={{ width: 300 }}>
            { isFetching ? 'Salvando. Aguarde...' : 'Salvar' }
          </button>
        </div>
      </div>
    </form>
  )
}

export default PartnerForm
